import {
  createInfiniteQuery,
  createQuery,
  createSuspenseQuery,
  type InfiniteQueryHookOptions,
} from 'react-query-kit';
import { type InfiniteData, keepPreviousData } from '@tanstack/react-query';

import type { QuizsetFolderCursorSearchReq } from '@/api/gen/_model/quizsetFolderCursorSearchReq';
import type { QuizsetFolderNextRes } from '@/api/gen/_model/quizsetFolderNextRes';
import {
  isRecoverableCursorError,
  useCursorQueryRecovery,
} from '@/shared/hooks/useCursorQueryRecovery';

import type { Folder } from '../types/folder.types';

import {
  fetchFolderDetail,
  fetchMyFolders,
  fetchNextFolderQuiz,
  type FolderCursorPage,
  type FolderSearchPage,
  searchMyFolders,
} from './fetchers';
import { folderKeys } from './folder.keys';

export type MyFoldersVariables = Omit<QuizsetFolderCursorSearchReq, 'cursor'>;

type MyFoldersQueryOptions = InfiniteQueryHookOptions<
  FolderSearchPage,
  Error,
  InfiniteData<FolderSearchPage, string | undefined>,
  MyFoldersVariables,
  string | undefined
>;

/**
 * 원본 쿼리. 키·옵션을 직접 다뤄야 하는 곳(테스트 등)만 쓰고, 화면은 아래 `useMyFolders` 를 쓴다.
 */
export const useMyFoldersQuery = createInfiniteQuery<
  FolderSearchPage,
  MyFoldersVariables,
  Error,
  string | undefined
>({
  queryKey: folderKeys.listSearch(),
  fetcher: (variables, { pageParam, signal }) =>
    searchMyFolders(variables, pageParam, signal),
  getNextPageParam: lastPage =>
    lastPage.hasNext ? lastPage.nextCursor : undefined,
  initialPageParam: undefined,
  placeholderData: keepPreviousData,
  throwOnError: error => !isRecoverableCursorError(error),
});

export const useMyFolders = (options?: MyFoldersQueryOptions) => {
  const query =
    useMyFoldersQuery<InfiniteData<FolderSearchPage, string | undefined>>(
      options,
    );
  const { isRecovering } = useCursorQueryRecovery({
    isError: query.isError,
    error: query.error,
    hasLoadedPages:
      !query.isPlaceholderData && (query.data?.pages.length ?? 0) > 0,
    refetch: query.refetch,
  });

  return {
    ...query,
    isFetchingNextPage: query.isFetchingNextPage || isRecovering,
  };
};

export const useMyFoldersWithItems = createInfiniteQuery<
  FolderCursorPage,
  void,
  Error,
  string | undefined
>({
  queryKey: folderKeys.listWithItems(),
  fetcher: (_variables, { pageParam }) => fetchMyFolders(pageParam),
  getNextPageParam: lastPage => lastPage.nextCursor,
  initialPageParam: undefined,
});

/**
 * 폴더 상세.
 *
 * **suspense 쿼리가 아니다.** 이 화면은 `getServerSideProps` 에서 상세를 미리 받아
 * `dehydrate` 로 넘기므로 클라이언트에는 로딩 구간이 사실상 없다. 그런데 SSR HTML 을
 * 하이드레이션하는 동안 Suspense 경계 안에서 상태가 바뀌면(인증 정보 도착 등) React 가
 * 서버 HTML 을 버리고 클라이언트에서 다시 그린다 — 공개 URL 을 SEO 대상으로 두려고
 * `NoSSR` 을 일부러 피한 이 페이지에서는 그 전환이 손해다.
 *
 * 퀴즈 상세(`useQuizDetail`, `api/quiz.ts`)도 같은 이유로 일반 `createQuery` 를 쓴다.
 */
export const useFolderDetail = createQuery<Folder, { id: string }>({
  queryKey: folderKeys.details(),
  fetcher: ({ id }) => fetchFolderDetail(id),
  staleTime: 30_000,
  refetchOnWindowFocus: false,
});

/**
 * 같은 상세를 **suspense 로** 읽는다. 캐시 키가 같아 `useFolderDetail` 과 캐시를 공유한다.
 *
 * `NoSSR` 로 감싼 화면 전용이다 — 편집(`/update/folder/[id]`)과 그 안의 퀴즈 추가 모달.
 * 서버 렌더가 없으니 하이드레이션 중 경계가 깨질 일이 없고, 로딩 UX 를 경계에 맡기는 편이
 * 화면마다 `if (!data) return null` 을 흩뿌리는 것보다 낫다.
 *
 * ⚠️ SSR 로 그리는 화면(폴더 공개 상세)에는 쓰지 말 것. 위 `useFolderDetail` 주석 참고.
 */
export const useFolderDetailSuspense = createSuspenseQuery<
  Folder,
  { id: string }
>({
  queryKey: folderKeys.details(),
  fetcher: ({ id }) => fetchFolderDetail(id),
  staleTime: 30_000,
  refetchOnWindowFocus: false,
});

/**
 * 폴더 재생에서 다음 퀴즈를 판정한다.
 *
 * 서버가 재생 커서를 들고 있지 않아(stateless) 지금 위치를 매번 함께 보낸다. 그래서 퀴즈에
 * 걸린 개인 '다음 퀴즈'가 아니라 **폴더의 최신 순서와 접근 상태**가 기준이 된다.
 *
 * POST 지만 서버 상태를 바꾸지 않는 조회라 query 로 둔다. 같은 위치에서 두 번 물어도 답이 같다.
 */
export const useFolderNextQuiz = createQuery<
  QuizsetFolderNextRes,
  { folderId: string; currentQuizsetId: string }
>({
  queryKey: folderKeys.playNext(),
  fetcher: ({ folderId, currentQuizsetId }) =>
    fetchNextFolderQuiz(folderId, currentQuizsetId),
  staleTime: 0,
  refetchOnWindowFocus: false,
});
